<template>
  <div class="order-payouts-list flex flex-col">
    <div class="fixed-section sticky top-[118px] w-full bg-gray-50 z-20">
      <!-- 總利潤 -->
      <div class="earning-information rounded-8px">
        <div class="w-full h-4 bg-bg-base"></div>
        <div class="header text-14px px-4 pt-2 bg-gray-600 rounded-t-8px font-medium text-gray-200">
          {{ $t('profiles.payout_items.total_profit') }}
        </div>
        <div class="content text-14px py-2 px-4 bg-gray-600 rounded-b-8px">
          <div class="text-20px lg:text-24px">
            {{ formatCurrency(totalUnpaid) }}
          </div>
        </div>
      </div>
      
      <!-- 搜尋 & 選擇日期 -->
      <div class="flex justify-between items-center my-4">
        <search-input
          v-model="state.currentSearch"
          :placeholder="$t('models.model_search_form.search_model')"
          @search="onSearch"
        />
  
        <date-range-picker
          v-model="state.dateRange"
          :placeholder="$t('profiles.payout_item_search_form.select_date_range')"
          ref="calendarRef"
          @visibility-change="isCalendarVisible = $event"
        />
      </div>
      <!-- 價格類型 filter -->
      <div class="text-14px p-1">
        <div class="flex">
          <button
            v-for="type in priceTypes"
            :key="type.type"
            class="flex items-center px-4 py-1 mr-1 font-medium rounded-full hover:bg-gray-400"
            :class="{ 'bg-gray-400': type.type === state.currentState }"
            @click="handleStateFilter(type.type)"
          >
            <div>
              {{ $t('profiles.payout_items.' + type.title) }}
            </div>
            <div class="inline-flex ml-2 px-1 h-6 min-w-[32px] rounded-[20px] bg-gray-500 justify-center items-center">
              <span>{{ type.count }}</span>
            </div>
          </button>
        </div>
      </div>
  
      <!-- 表頭 -->
      <div class="mt-4 text-12px grid grid-cols-[28fr_20fr_18fr_20fr_25fr_12fr_16fr] lg:grid-cols-[43fr_25fr_24fr_34fr_35fr_22fr_24fr]">
        <div class='h-5 text-grey-3 font-medium'>{{ $t('profiles.payout_items.model_title') }}</div>
        <div class='h-5 text-grey-3 font-medium'>{{ $t('profiles.payout_items.date') }}</div>
        <div class='h-5 text-grey-3 font-medium'>{{ $t('profiles.payout_items.revenues') }}</div>
        <div class='h-5 text-grey-3 font-medium'>{{ $t('profiles.payout_lists.platform_fee') }}</div>
        <div class='h-5 text-grey-3 font-medium'>{{ $t('profiles.payout_lists.pv_commission') }}</div>
        <div class='h-5 text-grey-3 font-medium col-span-1'>{{ $t('profiles.payout_lists.profit') }}</div>
        <div class='h-5 text-grey-3 font-medium'>{{ $t('profiles.payout_items.payment_state') }}</div>
        <hr class='col-span-7 border-gray-500 my-2'>
      </div>
    </div>

    <!-- loading 狀態 -->
    <div v-if="state.initialLoading" class="flex justify-center items-center py-8  mt-[15%] lg:mt-[20%] 2xl:mt-[15%]">
      <p class="text-16px font-medium text-center">
          <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
      </p>
    </div>

    <!-- 無資料狀態 -->
    <div v-else-if="items.length === 0" class="flex-1 flex flex-col items-center justify-center py-8 mt-[15%] lg:mt-[20%] 2xl:mt-[15%]">
      <img src="../../images/ClipboardText.svg" class="block w-12 h12 mb-4" />
      <p class="text-16px font-medium text-center">
        {{ $t('profiles.payout_items.no_payout_found') }}
      </p>
    </div>

    <!-- order payout list -->
    <div v-else class="items-list pr-3 -mr-3" ref="itemsContainer">
      <order-payout-item
        v-for="(item, index) in items"
        :key="item.id"
        :item="item"
        :is-last="index === items.length - 1"
      />
      
      <!-- loading -->
      <div v-if="state.loading" class="py-4 flex justify-center mt-[15%] lg:mt-[20%] 2xl:mt-[15%]">
        <p class="text-16px font-medium text-center">
          <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import debounce from 'lodash/debounce';

export default defineComponent({
  setup() {
    const itemsContainer = ref(null);
    const scrollListener = ref(null);
    const initialDataLoaded = ref(false);
    const priceTypes = ref([]);
    const totalUnpaid = ref(0);
    const calendarRef = ref(null);
    const isCalendarVisible = ref(false);

    // 狀態
    const state = reactive({
      loading: false,
      initialLoading: true,
      itemsCount: 10,
      currentSearch: '',
      currentState: 'all',
      dateRange: { start: '', end: '' },
    });

    const stateData = reactive({
      all: { items: [], count: 0, totalCount: 0 },
      paid: { items: [], count: 0, totalCount: 0 },
      free: { items: [], count: 0, totalCount: 0 }
    });

    const pageInfo = reactive({
      all: { page: 0, hasMore: true },
      paid: { page: 0, hasMore: true },
      free: { page: 0, hasMore: true },
    });

    const items = computed(() => stateData[state.currentState].items);
    const dataRangeDisplay = computed(() => {
      if (state.date_start && state.date_end) {
        return `${state.date_start} - ${state.date_end}`;
      }
      return '';
    });

    // 取得 API 查詢參數
    const getQueryParams = () => {
      const params = new URLSearchParams();
      if (state.dateRange.start) params.append('date_start', state.dateRange.start);
      if (state.dateRange.end) params.append('date_end', state.dateRange.end);
      return params;
    };
    
    // Fetches summary data (total counts, total unpaid) once
    const fetchInitialData = async () => {
      try {
        const params = new URLSearchParams({
          kw: state.currentSearch,
          date_start: getQueryParams().get('date_start') || '',
          date_end: getQueryParams().get('date_end') || '',
        });

        const response = await fetch(`/api/v1/payouts/summary?${params}`);
        if (!response.ok) throw new Error('Network response was not ok for initial summary data');
        const data = await response.json();

        priceTypes.value = data.post_price_types || [];
        totalUnpaid.value = data.total_profit || 0;

        stateData.all.totalCount = priceTypes.value.find(pt => pt.type === 'all')?.count || 0;
        stateData.paid.totalCount = priceTypes.value.find(pt => pt.type === 'paid')?.count || 0;
        stateData.free.totalCount = priceTypes.value.find(pt => pt.type === 'free')?.count || 0;
        
        initialDataLoaded.value = true;
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };

    // 取得資料
    const fetchPayoutItems = async (stateType = 'all', page = 1, perPage = 10) => {
      if (!pageInfo[stateType].hasMore || state.loading) return;

      state.loading = true;
      try {
        const params = new URLSearchParams({
          page: page + 1,
          per_page: perPage,
          price_type: stateType,
          kw: state.currentSearch,
          date_start: getQueryParams().get('date_start') || '',
          date_end: getQueryParams().get('date_end') || '',
        });

        const response = await fetch(`/api/v1/payouts/items?${params}`);
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        
        const newItems = data.items || [];
        // const initialLength = stateData[stateType].items.length;
        categorizeItems(newItems);
        // const addedItemsCount = stateData[stateType].items.length - initialLength;

        pageInfo[stateType].page++;
        pageInfo[stateType].hasMore = pageInfo[stateType].page < (data.pagination?.total_pages || 0);

        if (newItems.length < perPage && pageInfo[stateType].hasMore && stateData[stateType].count < stateData[stateType].totalCount) {
          await fetchPayoutItems(stateType, pageInfo[stateType].page, perPage);
        } else {
          // 延遲檢查頁面高度，確保DOM已更新
          setTimeout(() => {
            checkAndLoadMore(stateType, page, perPage);
          }, 100);
        }

      } catch (error) {
        console.error('Error fetching payout items:', error);
        pageInfo[stateType].hasMore = false;
      } finally {
        state.loading = false;
        state.initialLoading = false;
      }
    };

    function categorizeItems(newItems) {
      if (!Array.isArray(newItems)) return;
      newItems.forEach(item => {
        // all 狀態
        if (!stateData.all.items.some(existing => existing.id === item.id)) {
          stateData.all.items.push(item);
          stateData.all.count++;
        }
        // paid 狀態
        if (Number(item.price) > 0 && !stateData.paid.items.some(existing => existing.id === item.id)) {
          stateData.paid.items.push(item);
          stateData.paid.count++;
        }
        // free 狀態
        if (Number(item.price) === 0 && !stateData.free.items.some(existing => existing.id === item.id)) {
          stateData.free.items.push(item);
          stateData.free.count++;
        }
      });
    }

    const checkAndLoadMore = (stateType, page, perPage) => {
      if (!pageInfo[stateType].hasMore || state.loading) return;
      
      // 檢查頁面內容是否足夠產生滾動條
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      
      // 如果頁面高度不足以產生滾動條，繼續加載更多數據
      if (documentHeight <= windowHeight) {
        fetchPayoutItems(stateType, pageInfo[stateType].page, perPage);
      }
    };

    const resetDataForNewQuery = (preserveSearchTerm = false) => {
      if (!preserveSearchTerm) {
        state.currentSearch = '';
      }
      ['all', 'paid', 'free'].forEach(type => {
        stateData[type].items = [];
        stateData[type].count = 0; // Reset current loaded count
        // totalCount remains as it's from initial data
        pageInfo[type].page = 0;
        pageInfo[type].hasMore = true; // Assume more for new query until API says otherwise
      });
    };

    // 搜尋
    const onSearch = async () => {
      const { start, end } = state.dateRange;
      if (start || end) {
        sessionStorage.setItem('orderPayoutListDates', JSON.stringify({
          start: start || '',
          end: end || ''
        }));
      } else {
        sessionStorage.removeItem('orderPayoutListDates');
      }
      resetDataForNewQuery(true);
      state.currentState = 'all'; // Default to 'all' view for new search
      scrollToTop();
      if (initialDataLoaded.value) {
        await fetchInitialData();
        await fetchPayoutItems('all', 0, state.itemsCount);
      }
    };

    // 取消搜尋
    const onCancel = async () => {
      resetDataForNewQuery(false);
      state.currentState = 'all';
      scrollToTop();
      if (initialDataLoaded.value) {
        await fetchInitialData();
        await fetchPayoutItems('all', 0, state.itemsCount);
      }
    };


    // 切換價格類型
    const handleStateFilter = async (stateType) => {
      if (state.currentState === stateType) return;
      state.currentState = stateType;

      if (stateData[stateType].count >= stateData[stateType].totalCount) {
        scrollToTop();
        return;
      }

      if (pageInfo[stateType].hasMore) {
        await fetchPayoutItems(stateType, pageInfo[stateType].page, state.itemsCount);
      }
      scrollToTop();
    };

    // 格式化貨幣
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    };
    
    // 滾動到頂部
    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    };
    
    // 添加滾動監聽
    const addScrollListener = () => {
      removeScrollListener();
      scrollListener.value = handleScroll;
      window.addEventListener('scroll', scrollListener.value, { passive: true });
    };
    
    // 移除滾動監聽
    const removeScrollListener = () => {
      if (scrollListener.value) {
        window.removeEventListener('scroll', scrollListener.value);
        scrollListener.value = null;
      }
    };
    
    // 處理滾動事件
    const handleScroll = () => {
      if (calendarRef.value && isCalendarVisible.value) {
        calendarRef.value.hide();
      }
      
      debouncedLoadMore();
    };

    const debouncedLoadMore = debounce(() => {
      if (!pageInfo[state.currentState].hasMore) return;
      
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrolledPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;
      
      if (scrolledPercentage > 30 && !state.loading) {
        if (stateData[state.currentState].count >= stateData[state.currentState].totalCount) {
          return;
        }
        fetchPayoutItems(state.currentState, pageInfo[state.currentState].page, state.itemsCount);
      }
    }, 100);

    onMounted(() => {
      const storedDates = sessionStorage.getItem('orderPayoutListDates');
      if (storedDates) {
        try {
          const { start, end } = JSON.parse(storedDates);

          state.dateRange.start = start || '';
          state.dateRange.end = end || '';

        } catch (e) {
          sessionStorage.removeItem('orderPayoutListDates');
        }
      }
      fetchInitialData();
      fetchPayoutItems('all', 0, state.itemsCount);
      addScrollListener();
    });
    onUnmounted(() => {
      removeScrollListener();
    });
    

    return {
      state,
      stateData,
      items,
      dataRangeDisplay,
      priceTypes,
      totalUnpaid,
      itemsContainer,
      pageInfo,
      handleStateFilter,
      onSearch,
      onCancel,
      formatCurrency,
      calendarRef,
      isCalendarVisible
    };
  }
});
</script>

